import { throttle as U } from "lodash-es";
import { hexToFractionalRgb as p, lineStringToLines as g, pointsAndPointsToLines as x, subSetArray as L } from "@allmaps/stdlib";
import { black as R, white as A, pink as y, blue as w, gray as D, yellow as O } from "@allmaps/tailwind";
import v from "./TriangulatedWarpedMap.js";
import { WarpedMapEvent as I, WarpedMapEventType as S } from "../shared/events.js";
import { createTransform as G, invertTransform as b, applyTransform as u } from "../shared/matrix.js";
import { createBuffer as a } from "../shared/webgl2.js";
import { getTilesAtOtherScaleFactors as X, tileKey as F } from "../shared/tiles.js";
import "@allmaps/id";
import "@allmaps/annotation";
import "rbush";
import "@turf/boolean-point-in-polygon";
import "comlink";
import "@allmaps/transform";
const B = 200, V = {
  leading: !0,
  trailing: !0
}, C = 1, M = 1, N = 6, j = [...p(R), 1], W = 0, z = [...p(A), 1], Y = 16, Z = [...p(R), 1], K = 1, q = [...p(A), 1], H = 5, k = 1;
function de(m, e, r, o) {
  return (T, h, c) => new J(
    T,
    h,
    m,
    e,
    r,
    o,
    c
  );
}
class J extends v {
  gl;
  mapProgram;
  linesProgram;
  pointsProgram;
  mapVao = null;
  linesVao = null;
  pointsVao = null;
  lineLayers = [];
  pointLayers = [];
  // Consider to store cachedTilesByTileKey as a quadtree for faster lookups
  cachedTilesByTileKey = /* @__PURE__ */ new Map();
  cachedTilesByTileUrl = /* @__PURE__ */ new Map();
  cachedTilesForTexture = [];
  previousCachedTilesForTexture = [];
  opacity = C;
  saturation = M;
  renderOptions = {};
  cachedTilesTextureArray = null;
  cachedTilesResourceOriginPointsAndDimensionsTexture = null;
  cachedTilesScaleFactorsTexture = null;
  // About renderTransform and InvertedRenderTransform:
  // renderTransform is the product of:
  // - the viewport's projectedGeoToClipTransform (projected geo coordinates -> clip coordinates)
  // - the saved invertedRenderTransform (projected clip coordinates -> geo coordinates)
  // since updateVertexBuffers ('where to draw triangles') run with possibly a different Viewport then renderInternal ('drawing the triangles'), a difference caused by throttling, there needs to be an adjustment.
  // this adjustment is minimal: indeed, since invertedRenderTransform is set as the inverse of the viewport's projectedGeoToClipTransform in updateVertexBuffers()
  // this renderTransform is almost the identity transform [1, 0, 0, 1, 0, 0].
  invertedRenderTransform;
  throttledUpdateTextures;
  /**
   * Creates an instance of WebGL2WarpedMap.
   *
   * @constructor
   * @param {string} mapId - ID of the map
   * @param {GeoreferencedMap} georeferencedMap - Georeferenced map used to construct the WarpedMap
   * @param {WebGL2RenderingContext} gl - WebGL rendering context
   * @param {WebGLProgram} mapProgram - WebGL program for map
   * @param {Partial<WarpedMapOptions>} options - WarpedMapOptions
   */
  constructor(e, r, o, T, h, c, i) {
    super(e, r, i), this.gl = o, this.initializeWebGL(T, h, c), this.invertedRenderTransform = G(), this.throttledUpdateTextures = U(
      this.updateTextures.bind(this),
      B,
      V
    );
  }
  initializeWebGL(e, r, o) {
    this.mapProgram = e, this.linesProgram = r, this.pointsProgram = o, this.mapVao = this.gl.createVertexArray(), this.linesVao = this.gl.createVertexArray(), this.pointsVao = this.gl.createVertexArray(), this.cachedTilesTextureArray = this.gl.createTexture(), this.cachedTilesScaleFactorsTexture = this.gl.createTexture(), this.cachedTilesResourceOriginPointsAndDimensionsTexture = this.gl.createTexture();
  }
  /**
   * Update the vertex buffers of this warped map
   *
   * @param {Transform} projectedGeoToClipTransform - Transform from projected geo coordinates to webgl2 coordinates in the [-1, 1] range. Equivalent to OpenLayers' projectionTransform.
   */
  updateVertexBuffers(e) {
    this.invertedRenderTransform = b(e), this.updateVertexBuffersMap(e);
  }
  /**
   * Clear textures for this map
   */
  clearTextures() {
    this.throttledUpdateTextures();
  }
  /**
   * Add cached tile to the textures of this map and update textures
   *
   * @param {CachedTile} cachedTile
   */
  addCachedTileAndUpdateTextures(e) {
    this.cachedTilesByTileKey.set(e.tileKey, e), this.cachedTilesByTileUrl.set(e.tileUrl, e), this.throttledUpdateTextures();
  }
  /**
   * Remove cached tile from the textures of this map and update textures
   *
   * @param {string} tileUrl
   */
  removeCachedTileAndUpdateTextures(e) {
    const r = this.cachedTilesByTileUrl.get(e);
    r && this.cachedTilesByTileKey.delete(r.tileKey), this.cachedTilesByTileUrl.delete(e), this.throttledUpdateTextures();
  }
  cancelThrottledFunctions() {
    this.throttledUpdateTextures.cancel();
  }
  destroy() {
    this.gl.deleteVertexArray(this.mapVao), this.gl.deleteVertexArray(this.linesVao), this.gl.deleteVertexArray(this.pointsVao), this.gl.deleteTexture(this.cachedTilesTextureArray), this.gl.deleteTexture(this.cachedTilesScaleFactorsTexture), this.gl.deleteTexture(
      this.cachedTilesResourceOriginPointsAndDimensionsTexture
    ), this.cancelThrottledFunctions(), super.destroy();
  }
  setLineLayers() {
    this.lineLayers = [
      {
        projectedGeoLines: g(
          this.projectedGeoTriangulationMask
        ),
        projectedGeoPreviousLines: g(
          this.projectedGeoPreviousTriangulationMask
        ),
        viewportSize: 8,
        color: [...p(y), 1]
      },
      {
        projectedGeoLines: x(
          this.projectedGeoPoints,
          this.projectedGeoTransformedResourcePoints
        ),
        projectedGeoPreviousLines: x(
          this.projectedGeoPoints,
          this.projectedGeoPreviousTransformedResourcePoints
        ),
        color: [...p(R), 1],
        borderColor: [...p(A), 1]
      }
    ];
  }
  setPointLayers() {
    this.pointLayers = [
      {
        projectedGeoPoints: this.projectedGeoPoints,
        color: [...p(w), 1]
      },
      {
        projectedGeoPoints: this.projectedGeoTransformedResourcePoints,
        projectedGeoPreviousPoints: this.projectedGeoPreviousTransformedResourcePoints,
        color: [...p(y), 1]
      },
      {
        projectedGeoPoints: this.projectedGeoPreviousTrianglePoints,
        color: [...p(D), 1]
      },
      {
        projectedGeoPoints: this.projectedGeoTrianglePoints,
        color: [...p(O), 1]
      }
    ];
  }
  updateVertexBuffersMap(e) {
    if (!this.mapVao)
      return;
    const r = this.gl, o = this.mapProgram;
    r.bindVertexArray(this.mapVao), a(
      r,
      o,
      new Float32Array(this.resourceTrianglePoints.flat()),
      2,
      "a_resourceTrianglePoint"
    );
    const T = this.projectedGeoPreviousTrianglePoints.map(
      (i) => u(e, i)
    );
    a(
      r,
      o,
      new Float32Array(T.flat()),
      2,
      "a_clipPreviousTrianglePoint"
    );
    const h = this.projectedGeoTrianglePoints.map(
      (i) => u(e, i)
    );
    a(
      r,
      o,
      new Float32Array(h.flat()),
      2,
      "a_clipTrianglePoint"
    ), a(
      r,
      o,
      new Float32Array(this.previousTrianglePointsDistortion),
      1,
      "a_previousTrianglePointDistortion"
    ), a(
      r,
      o,
      new Float32Array(this.trianglePointsDistortion),
      1,
      "a_trianglePointDistortion"
    );
    const c = new Float32Array(
      this.resourceTrianglePoints.length
    ).map((i, d) => d);
    a(
      r,
      o,
      c,
      1,
      "a_trianglePointIndex"
    );
  }
  updateVertexBuffersLines(e) {
    if (!this.linesVao)
      return;
    const r = this.gl, o = this.linesProgram;
    r.bindVertexArray(this.linesVao), this.setLineLayers();
    const T = this.lineLayers.reduce(
      (t, s) => t.concat(s.projectedGeoLines),
      []
    ).map((t) => [
      t[0],
      t[0],
      t[0],
      t[1],
      t[1],
      t[1]
    ]).flat().map((t) => u(e, t));
    a(
      r,
      o,
      new Float32Array(T.flat()),
      2,
      "a_clipPoint"
    );
    const h = this.lineLayers.reduce(
      (t, s) => t.concat(s.projectedGeoLines),
      []
    ).map((t) => [
      t[1],
      t[1],
      t[1],
      t[0],
      t[0],
      t[0]
    ]).flat().map((t) => u(e, t));
    a(
      r,
      o,
      new Float32Array(h.flat()),
      2,
      "a_clipOtherPoint"
    );
    const c = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoPreviousLines || s.projectedGeoLines
      ),
      []
    ).map((t) => [
      t[0],
      t[0],
      t[0],
      t[1],
      t[1],
      t[1]
    ]).flat().map((t) => u(e, t));
    a(
      r,
      o,
      new Float32Array(c.flat()),
      2,
      "a_clipPreviousPoint"
    );
    const i = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoPreviousLines || s.projectedGeoLines
      ),
      []
    ).map((t) => [
      t[1],
      t[1],
      t[1],
      t[0],
      t[0],
      t[0]
    ]).flat().map((t) => u(e, t));
    a(
      r,
      o,
      new Float32Array(i.flat()),
      2,
      "a_clipPreviousOtherPoint"
    );
    const d = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap((P) => [
          0,
          0,
          1,
          0,
          0,
          1
        ])
      ),
      []
    );
    a(
      r,
      o,
      new Float32Array(d),
      1,
      "a_isOtherPoint"
    );
    const E = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap((P) => [
          1,
          -1,
          1,
          1,
          -1,
          1
        ])
      ),
      []
    );
    a(
      r,
      o,
      new Float32Array(E),
      1,
      "a_normalSign"
    );
    const n = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap(
          (P) => Array(6).fill(
            Object.prototype.hasOwnProperty.call(s, "viewportSize") ? s.viewportSize : N
          )
        )
      ),
      []
    );
    a(
      r,
      o,
      new Float32Array(n),
      1,
      "a_viewportSize"
    );
    const l = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap(
          (P) => Array(6).fill(
            Object.prototype.hasOwnProperty.call(s, "color") ? s.color : j
          )
        )
      ),
      []
    );
    a(r, o, new Float32Array(l.flat()), 4, "a_color");
    const _ = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap(
          (P) => Array(6).fill(
            s.viewportBorderSize || W
          )
        )
      ),
      []
    );
    a(
      r,
      o,
      new Float32Array(_),
      1,
      "a_viewportBorderSize"
    );
    const f = this.lineLayers.reduce(
      (t, s) => t.concat(
        s.projectedGeoLines.flatMap(
          (P) => Array(6).fill(
            Object.prototype.hasOwnProperty.call(s, "borderColor") ? s.borderColor : z
          )
        )
      ),
      []
    );
    a(
      r,
      o,
      new Float32Array(f.flat()),
      4,
      "a_borderColor"
    );
  }
  updateVertexBuffersPoints(e) {
    if (!this.pointsVao)
      return;
    const r = this.gl, o = this.pointsProgram;
    r.bindVertexArray(this.pointsVao), this.setPointLayers();
    const T = this.pointLayers.reduce(
      (n, l) => n.concat(l.projectedGeoPoints),
      []
    ).map((n) => u(e, n));
    a(
      r,
      o,
      new Float32Array(T.flat()),
      2,
      "a_clipPoint"
    );
    const h = this.pointLayers.reduce(
      (n, l) => n.concat(
        l.projectedGeoPreviousPoints || l.projectedGeoPoints
      ),
      []
    ).map((n) => u(e, n));
    a(
      r,
      o,
      new Float32Array(h.flat()),
      2,
      "a_clipPreviousPoint"
    );
    const c = this.pointLayers.reduce(
      (n, l) => n.concat(
        l.projectedGeoPoints.map(
          (_) => Object.prototype.hasOwnProperty.call(l, "viewportSize") ? l.viewportSize : Y
        )
      ),
      []
    );
    a(
      r,
      o,
      new Float32Array(c),
      1,
      "a_viewportSize"
    );
    const i = this.pointLayers.reduce(
      (n, l) => n.concat(
        l.projectedGeoPoints.map(
          (_) => Object.prototype.hasOwnProperty.call(l, "color") ? l.color : Z
        )
      ),
      []
    );
    a(r, o, new Float32Array(i.flat()), 4, "a_color");
    const d = this.pointLayers.reduce(
      (n, l) => n.concat(
        l.projectedGeoPoints.map(
          (_) => l.viewportBorderSize || K
        )
      ),
      []
    );
    a(
      r,
      o,
      new Float32Array(d),
      1,
      "a_viewportBorderSize"
    );
    const E = this.pointLayers.reduce(
      (n, l) => n.concat(
        l.projectedGeoPoints.map(
          (_) => Object.prototype.hasOwnProperty.call(l, "borderColor") ? l.borderColor : q
        )
      ),
      []
    );
    a(
      r,
      o,
      new Float32Array(E.flat()),
      4,
      "a_borderColor"
    );
  }
  async updateTextures() {
    const e = this.gl;
    if (this.updateCachedTilesForTextures(), this.cachedTilesForTexture.length !== 0 && L(
      this.previousCachedTilesForTexture.map(
        (i) => i.tileUrl
      ),
      this.cachedTilesForTexture.map((i) => i.tileUrl)
    ))
      return;
    const r = Math.max(
      ...this.parsedImage.tileZoomLevels.map((i) => i.width)
    ), o = Math.max(
      ...this.parsedImage.tileZoomLevels.map((i) => i.height)
    ), T = this.cachedTilesForTexture.length;
    e.pixelStorei(e.UNPACK_ALIGNMENT, 4), e.bindTexture(e.TEXTURE_2D_ARRAY, this.cachedTilesTextureArray), e.texImage3D(
      e.TEXTURE_2D_ARRAY,
      0,
      e.RGBA,
      r,
      o,
      T,
      0,
      e.RGBA,
      e.UNSIGNED_BYTE,
      null
    );
    for (let i = 0; i < this.cachedTilesForTexture.length; i++) {
      const d = this.cachedTilesForTexture[i].data, E = e.createBuffer();
      e.bindBuffer(e.PIXEL_UNPACK_BUFFER, E), e.bufferData(e.PIXEL_UNPACK_BUFFER, d.data, e.STATIC_DRAW), e.texSubImage3D(
        e.TEXTURE_2D_ARRAY,
        0,
        0,
        0,
        i,
        d.width,
        d.height,
        1,
        e.RGBA,
        e.UNSIGNED_BYTE,
        0
      ), e.bindBuffer(e.PIXEL_UNPACK_BUFFER, null);
    }
    e.texParameteri(e.TEXTURE_2D_ARRAY, e.TEXTURE_MIN_FILTER, e.LINEAR), e.texParameteri(e.TEXTURE_2D_ARRAY, e.TEXTURE_MAG_FILTER, e.LINEAR), e.texParameteri(e.TEXTURE_2D_ARRAY, e.TEXTURE_WRAP_S, e.CLAMP_TO_EDGE), e.texParameteri(e.TEXTURE_2D_ARRAY, e.TEXTURE_WRAP_T, e.CLAMP_TO_EDGE);
    const h = this.cachedTilesForTexture.map((i) => {
      if (i && i.imageRequest && i.imageRequest.region)
        return [
          i.imageRequest.region.x,
          i.imageRequest.region.y,
          i.imageRequest.region.width,
          i.imageRequest.region.height
        ];
    });
    e.bindTexture(
      e.TEXTURE_2D,
      this.cachedTilesResourceOriginPointsAndDimensionsTexture
    ), e.texImage2D(
      e.TEXTURE_2D,
      0,
      e.R32I,
      1,
      this.cachedTilesForTexture.length * 4,
      0,
      e.RED_INTEGER,
      e.INT,
      new Int32Array(h.flat())
    ), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_MIN_FILTER, e.NEAREST), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_MAG_FILTER, e.NEAREST), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_WRAP_S, e.CLAMP_TO_EDGE), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_WRAP_T, e.CLAMP_TO_EDGE);
    const c = this.cachedTilesForTexture.map(
      (i) => i.tile.tileZoomLevel.scaleFactor
    );
    e.bindTexture(e.TEXTURE_2D, this.cachedTilesScaleFactorsTexture), e.texImage2D(
      e.TEXTURE_2D,
      0,
      e.R32I,
      1,
      this.cachedTilesForTexture.length,
      0,
      e.RED_INTEGER,
      e.INT,
      new Int32Array(c)
    ), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_MIN_FILTER, e.NEAREST), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_MAG_FILTER, e.NEAREST), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_WRAP_S, e.CLAMP_TO_EDGE), e.texParameteri(e.TEXTURE_2D, e.TEXTURE_WRAP_T, e.CLAMP_TO_EDGE), this.dispatchEvent(new I(S.TEXTURESUPDATED));
  }
  updateCachedTilesForTextures() {
    const e = [], r = [], o = [];
    for (const c of this.fetchableTilesForViewport) {
      const i = this.cachedTilesByTileUrl.get(c.tileUrl);
      if (i)
        e.push(i);
      else
        for (const d of this.getCachedTilesAtOtherScaleFactors(
          c.tile
        ))
          r.push(d);
    }
    for (const c of this.overviewFetchableTilesForViewport) {
      const i = this.cachedTilesByTileUrl.get(c.tileUrl);
      if (i) {
        const d = this.tileZoomLevelForViewport ? this.tileZoomLevelForViewport.rows * this.tileZoomLevelForViewport.columns : void 0;
        (e.length == 0 || d && e.length < d) && o.push(i);
      }
    }
    let T = [
      ...e,
      ...r,
      ...o
    ];
    const h = /* @__PURE__ */ new Map();
    T.forEach(
      (c) => h.set(c.tileUrl, c)
    ), T = [...h.values()], this.previousCachedTilesForTexture = this.cachedTilesForTexture, this.cachedTilesForTexture = T;
  }
  getCachedTilesAtOtherScaleFactors(e) {
    if (this.cachedTilesByTileUrl.size == 0)
      return [];
    if (!this.tileZoomLevelForViewport)
      return [];
    const r = [];
    for (e of X(
      e,
      this.parsedImage,
      this.tileZoomLevelForViewport.scaleFactor,
      k,
      H,
      this.tileInCachedTiles.bind(this)
      // Only consider tiles in cache,
    )) {
      const o = this.tileToCachedTile(e);
      if (o)
        r.push(o);
      else
        throw new Error("Tile supposed to be in cache isn't.");
    }
    return r;
  }
  // Lookup by tileKey (zoomlevel, row, column) instead of tileUrl
  // Because computing the tileUrl for every tile is expensive
  tileToCachedTile(e) {
    return this.cachedTilesByTileKey.get(F(e));
  }
  tileInCachedTiles(e) {
    return this.cachedTilesByTileKey.has(F(e));
  }
}
export {
  de as createWebGL2WarpedMapFactory,
  J as default
};
//# sourceMappingURL=WebGL2WarpedMap.js.map
